import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { Subject, debounceTime } from 'rxjs';
import { DispatchConfirmInfo } from '../models/DispatchConfirmInfo';
import { FormStatus } from '../models/Enums';
import { ErrorState, SendDispatchFormState } from '../models/FormState';
import { ScheduleParameters } from '../models/ScheduleParameters';
import { TruckerConfirmItem } from '../models/TruckerConfirmItem';
import { TruckerConfirmParameters } from '../models/TruckerConfirmParameters';
import { TruckerConfirmRequest } from '../models/TruckerConfirmRequest';
import { TruckerContact } from '../models/TruckerContact';
import { TruckerConfirmationService } from '../services/trucker-confirmation.service';
import { ListItem } from '../models/ListItem';

import { ContactPhoneSelection } from '../models/ContactPhoneSelection';
import { TruckerConfirmResults } from '../models/TruckerConfirmResults';

@Component({
    selector: 'confirm-truckers',
    templateUrl: 'confirm-truckers.component.html',
    styles: [],
    standalone: false
})
export class ConfirmTruckersComponent implements OnInit, OnChanges {
    dispatchConfirmInfo: any;

    constructor(private _truckerConfirmService: TruckerConfirmationService) { }

    @ViewChildren('truckerContactDropdown') truckerContactDropdownRefs: QueryList<ElementRef>;
    @ViewChild('scoularContactDropdown') scoularContactDropdownRef: ElementRef;
    @ViewChild('closeButton') closeButton;
    @Input() scheduleParameters: ScheduleParameters = new ScheduleParameters();
    @Input() truckerId: number;
    @Input() refresh: boolean = false;
    @Input() confirmTruckersFormId: string = 'confirmTruckersModal';
    @Output() sendComplete: EventEmitter<number[]> = new EventEmitter<number[]>();
    sentScheduleIds: number[] = [];

    errorMessage: string = '';
    readonly FormStatus = FormStatus;
    formStatus: FormStatus = FormStatus.Normal;
    formState: SendDispatchFormState = new SendDispatchFormState();

    confirmInfo: DispatchConfirmInfo = new DispatchConfirmInfo();
    confirmParameters: TruckerConfirmParameters = new TruckerConfirmParameters();
    confirmRequest: TruckerConfirmRequest = new TruckerConfirmRequest();

    truckerList: TruckerConfirmItem[] = [];
    groupUserList: ListItem[] = [];
    confirmSendComplete: boolean = false;
    confirmResults: TruckerConfirmResults = new TruckerConfirmResults();

    loadsStatusModalId: string = this.confirmTruckersFormId + '-LoadsStatusModal';
    loadsStatusDispatchId: number = 0;
    loadsStatusRefresh: boolean = false;
    textSent: boolean = null;
    emailSent: boolean = null;
    scoularContactSearch: string = "";
    showScoularContact: boolean = false;
    scoularTruckertSearch: string = "";

    private intervalId: any;
    private sendClick = new Subject<void>();

    @HostListener('document:click', ['$event'])
    onClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        this.handleDropdownClicks(target);
    }

    private handleDropdownClicks(target: HTMLElement): void {
        const refs = this.truckerContactDropdownRefs?.toArray() ?? [];
        const truckers = this.truckerList ?? [];

        refs.forEach((ref, index) => {
            const trucker = truckers[index];
            if (trucker?.showTruckerContact && !ref.nativeElement.contains(target)) {
                this.closeTruckerContactSearch(trucker);
            }
        });

        if (this.showScoularContact && this.scoularContactDropdownRef && !this.scoularContactDropdownRef.nativeElement.contains(target)) {
            this.closeScoularContactSearch();
        }
    }

    private closeTruckerContactSearch(trucker: TruckerConfirmItem): void {
        trucker.showTruckerContact = false;
        trucker.truckerContactSearch = "";
    }

    private closeScoularContactSearch(): void {
        this.showScoularContact = false;
        this.scoularContactSearch = "";
    }

    private closeAllTruckerContactSearches(): void {
        this.truckerList.forEach(t => this.closeTruckerContactSearch(t));
    }

    toggleContactSearch(): void {
        this.closeAllTruckerContactSearches();
        this.showScoularContact = !this.showScoularContact;
        this.scoularContactSearch = "";
    }

    toggleTruckerSearch(trucker: TruckerConfirmItem): void {
        this.truckerList.forEach(t => {
            if (t !== trucker) {
                this.closeTruckerContactSearch(t);
            }
        });

        trucker.showTruckerContact = !trucker.showTruckerContact;
        if (!trucker.showTruckerContact) {
            trucker.truckerContactSearch = "";
        }
    }

    selectTrucker(trucker: TruckerConfirmItem) {
        trucker.dispatches.forEach(dispatch => {
            dispatch.selected = trucker.selected;
        });
        this.updateTruckerSelection(trucker);
    }

    selectDispatch(trucker: TruckerConfirmItem) {
        this.updateTruckerSelection(trucker);
    }

    get anySelectedDispatchCompuweighEligible(): boolean {
        return this.truckerList.some(trucker =>
            trucker.dispatches.some(d => d.selected && d.compuweighEligible)
        );
    }

    get hasNonEligibleSelectedDispatches(): boolean {
        return this.truckerList.some(trucker =>
            trucker.dispatches.some(d => d.selected && !d.compuweighEligible)
        );
    }

    updateTruckerSelection(trucker: TruckerConfirmItem): void {
        this.getTruckerSelectedDispatchIds(trucker);
        this.autoSelectSendToRoger();
        this.autoSelectSendToCompuweigh();
        this.validateDispatches();
        this.validateTruckerContacts();
        this.updateTextToggleBasedOnPhoneAvailability();
    }

    autoSelectSendToCompuweigh(): void {
        this.confirmRequest.sendCompuweigh = this.anySelectedDispatchCompuweighEligible;
    }

    updateTextToggleBasedOnPhoneAvailability(): void {
        this.confirmRequest.sendText = this.hasValidTextPhonesForSelectedTruckers() && this.getSelectedTruckers().length > 0;
    }

    hasValidTextPhonesForSelectedTruckers(): boolean {
        const selectedTruckers = this.getSelectedTruckers();
        if (selectedTruckers.length === 0) {
            return false;
        }

        return selectedTruckers.some(trucker => this.truckerHasValidPhones(trucker));
    }

    getSelectedTruckers(): TruckerConfirmItem[] {
        return this.truckerList.filter(trucker =>
            trucker.dispatches.some(dispatch => dispatch.selected)
        );
    }

    truckerHasValidPhones(trucker: TruckerConfirmItem): boolean {
        if (trucker.truckerContactIds.length === 0) {
            return false;
        }

        return trucker.truckerContactIds.some(contactId => {
            const contact = trucker.truckerContacts.find(c => c.contactId === contactId);
            if (!contact) return false;

            return this.contactHasValidPhones(contact, contactId, trucker);
        });
    }

    contactHasValidPhones(contact: TruckerContact, contactId: number, trucker: TruckerConfirmItem): boolean {
        const phoneSelection = this.confirmRequest.selectedPhones.find(p => p.contactId === contactId && p.truckerId === trucker.truckerId);
        if (phoneSelection) {
            const phone1Valid = phoneSelection.phone1Selected && contact.isPhone1TextEnabled && contact.phone1?.trim().length > 0;
            const phone2Valid = phoneSelection.phone2Selected && contact.isPhone2TextEnabled && contact.phone2?.trim().length > 0;

            return phone1Valid || phone2Valid;
        }

        return false;
    }

    hasSelectedTextablePhones(phoneSelection: ContactPhoneSelection, contact: TruckerContact): boolean {
        return (phoneSelection.phone1Selected && contact.isPhone1TextEnabled && contact.phone1?.trim().length > 0) ||
            (phoneSelection.phone2Selected && contact.isPhone2TextEnabled && contact.phone2?.trim().length > 0);
    }

    getTruckerSelectedDispatchIds(trucker: TruckerConfirmItem) {
        trucker.selectedDispatchIds = trucker.dispatches
            .filter(x => x.selected)
            .map(x => x.dispatchId.toString())
            .join(',');
    }

    autoSelectSendToRoger() {
        this.getSelectedDispatches();
        this.confirmRequest.sendRoger = this.confirmRequest.confirmItems.some(x => x.rogerEnabled);
    }

    truckerContactChange() {
        this.validateTruckerContacts();
        this.confirmRequest.sendText = this.hasValidTextPhones();
    }

    scoularContactChange() {
        this.validateScoularContact();
    }

    sendEmailChange() {
        this.validateTruckerContacts();
        this.validateDispatches();
    }

    sendRogerChange() {
        this.validateDispatches();
    }

    sendTextChange() {
        this.validateTruckerContacts();
        this.validateDispatches();
    }

    setErrorState(errorState: ErrorState, condition: boolean, message: string): void {
        errorState.invalid = condition;
        errorState.errorMessage = condition ? message : '';
    }

    validateScoularContact() {
        this.formState.scoularContact = new ErrorState();
        if (this.formState.submitted && this.confirmRequest.scoularUserIds?.length === 0) {
            this.setErrorState(this.formState.scoularContact, true, 'Scoular contact is required');
        }
    }

    validateSendOptions() {
        this.formState.sendOptions = new ErrorState();
        const noOptionsSelected = !this.confirmRequest.sendEmail && !this.confirmRequest.sendRoger && !this.confirmRequest.sendText && !this.confirmRequest.sendCompuweigh;
        if (this.formState.submitted && noOptionsSelected) {
            this.setErrorState(this.formState.sendOptions, true, 'At least 1 send option must be selected');
        }
    }

    validateDispatches() {
        this.formState.dispatch = new ErrorState();
        if (!this.formState.submitted) return;

        if (this.confirmRequest.confirmItems.length === 0) {
            this.setErrorState(this.formState.dispatch, true, 'You must select at least 1 dispatch to send');
            return;
        }

        const emailInProcess = this.hasDispatchesInProcess('email');
        const rogerInProcess = this.hasDispatchesInProcess('roger');
        const hasConflict = (this.confirmRequest.sendEmail && emailInProcess) || (this.confirmRequest.sendRoger && rogerInProcess);

        if (hasConflict) {
            this.setErrorState(this.formState.dispatch, true, 'You cannot send a dispatch that is already in process.');
        }
    }

    hasDispatchesInProcess(type: 'email' | 'roger'): boolean {
        return this.confirmRequest.confirmItems.some(t =>
            t.dispatches.some(d => type === 'email' ? d.emailInProcess : d.rogerInProcess)
        );
    }

    validateTruckerContacts() {
        this.formState.truckerContact = new ErrorState();

        if (!this.shouldValidateTruckerContacts()) {
            this.truckerList.forEach(trucker => trucker.contactError = false);
            return;
        }

        const validationResult = this.validateAllTruckerContacts();
        this.setTruckerContactErrors(validationResult);
    }

    shouldValidateTruckerContacts(): boolean {
        return this.formState.submitted && (this.confirmRequest.sendEmail || this.confirmRequest.sendText);
    }

    validateAllTruckerContacts() {
        let missingTruckerContact = false;
        let missingTruckerEmail = false;
        let missingTruckerPhone = false;

        for (let truckerItem of this.truckerList) {
            truckerItem.contactError = false;
            const selectedDispatches = truckerItem.dispatches.filter(dispatch => dispatch.selected);

            if (selectedDispatches.length === 0) continue;

            if (truckerItem.truckerContactIds.length === 0) {
                missingTruckerContact = true;
                truckerItem.contactError = true;
                continue;
            }

            const contactValidation = this.validateTruckerContactRequirements(truckerItem);
            if (this.confirmRequest.sendEmail && !contactValidation.hasEmail) {
                missingTruckerEmail = true;
                truckerItem.contactError = true;
            }
            if (this.confirmRequest.sendText && !contactValidation.hasSelectedPhone) {
                missingTruckerPhone = true;
                truckerItem.contactError = true;
            }
        }

        return { missingTruckerContact, missingTruckerEmail, missingTruckerPhone };
    }

    validateTruckerContactRequirements(truckerItem: TruckerConfirmItem) {
        let hasEmail = false;
        let hasSelectedPhone = false;

        for (let contactId of truckerItem.truckerContactIds) {
            const contact = truckerItem.truckerContacts.find(x => x.contactId === contactId);
            if (!contact) continue;

            if (contact.email.length > 0) {
                hasEmail = true;
            }

            if (this.confirmRequest.sendText) {
                if (this.contactHasValidPhones(contact, contactId, truckerItem)) {
                    hasSelectedPhone = true;
                }
            } else {
                const phoneSelection = this.confirmRequest.selectedPhones.find(p =>
                    p.contactId === contactId && p.truckerId === truckerItem.truckerId
                );
                if (phoneSelection && (phoneSelection.phone1Selected || phoneSelection.phone2Selected)) {
                    hasSelectedPhone = true;
                }
            }
        }

        return { hasEmail, hasSelectedPhone };
    }

    setTruckerContactErrors({ missingTruckerContact, missingTruckerEmail, missingTruckerPhone }) {
        if (missingTruckerContact) {
            this.setErrorState(this.formState.truckerContact, true,
                'Missing a trucker contact selection for at least one selected dispatch.');
            return;
        }

        let errorMessage = '';
        if (missingTruckerEmail) {
            errorMessage = 'A selected trucker contact is missing an email address';
        }
        if (missingTruckerPhone) {
            errorMessage += missingTruckerEmail ? ' and phone number.' : 'A selected trucker contact is missing phone number for text.';
        }

        if (errorMessage) {
            this.setErrorState(this.formState.truckerContact, true, errorMessage);
        }
    }

    getSelectedDispatches() {
        this.confirmRequest.confirmItems = this.truckerList
            .map(truckerItem => {
                const selectedDispatches = truckerItem.dispatches.filter(dispatch => dispatch.selected);
                if (selectedDispatches.length === 0) return null;

                const truckerPhoneSelections = this.confirmRequest.selectedPhones.filter(
                    ps => ps.truckerId === truckerItem.truckerId
                );

                return {
                    ...truckerItem,
                    truckerContacts: [],
                    dispatches: selectedDispatches,
                    selectedPhones: truckerPhoneSelections
                };
            })
            .filter(item => item !== null);
    }

    setDispatchProcessStatus(dispatch: any, sendEmail: boolean, sendRoger: boolean, rogerEnabled: boolean): void {
        if (sendEmail) {
            dispatch.emailInProcess = true;
            dispatch.emailStatus = 'In Process';
        }
        if (sendRoger && rogerEnabled) {
            dispatch.rogerInProcess = true;
            dispatch.rogerStatus = 'In Process';
        }
    }

    setDispatchesInProcess() {
        this.truckerList.forEach(truckerItem => {
            truckerItem.dispatches.forEach(dispatch => {
                if (dispatch.selected) {
                    this.setDispatchProcessStatus(dispatch, this.confirmRequest.sendEmail, this.confirmRequest.sendRoger, truckerItem.rogerEnabled);
                }
            });
        });
    }

    validateForm() {
        this.validateScoularContact();
        this.validateSendOptions();
        this.validateDispatches();
        this.validateTruckerContacts();
    }

    onSendClick() {
        this.sendClick.next();
    }

    sendConfirmation(): void {
        this.confirmSendComplete = false;
        this.getSelectedDispatches();
        this.formState.submitted = true;
        this.validateForm();

        if (!this.formState.isValid()) {
            return;
        }

        this.formStatus = FormStatus.Saving;
        this.setDispatchesInProcess();
        this.trackSentScheduleIds();
        this.startRefreshInterval();

        this.sendConfirmations();
        this.formState.submitted = false;
    }

    sendConfirmations(): void {
        if (this.confirmRequest.sendRoger) {
            this.sendRogerConfirmation();
        }

        if (this.confirmRequest.sendEmail) {
            this.sendEmailConfirmation();
        }

        if (this.confirmRequest.sendText) {
            this.sendTextConfirmation();
        }

        if (this.confirmRequest.sendCompuweigh) {
            this.sendCompuweighConfirmation();
        }
    }

    sendRogerConfirmation(): void {
        this._truckerConfirmService.sendRogerConfirmation(this.confirmRequest).subscribe({
            next: data => {
                this.confirmResults.rogerSendComplete = data.rogerSendComplete;
                this.confirmResults.rogerSuccess = data.rogerSuccess;
                this.confirmResults.rogerResult = data.rogerResult;
                this.formStatus = FormStatus.Normal;
            },
            error: err => this.errorMessage = err
        });
    }

    sendEmailConfirmation(): void {
        this.emailSent = false;
        this._truckerConfirmService.sendEmailConfirmation(this.confirmRequest).subscribe({
            next: data => { },
            error: err => this.errorMessage = err
        });
        this.emailSent = true;
    }

    sendTextConfirmation(): void {
        this._truckerConfirmService.sendTextConfirmation(this.confirmRequest).subscribe({
            next: data => {
                this.textSent = data.textResult;
                this.formStatus = FormStatus.Normal;
            },
            error: err => {
                this.errorMessage = err;
                this.setErrorState(this.formState.sendOptions, true, 'Error occurred while sending text message.');
            }
        });
    }

    sendCompuweighConfirmation(): void {
        this._truckerConfirmService.sendCompuweighConfirmation(this.confirmRequest).subscribe({
            next: data => {
                this.confirmResults.compuweighSendComplete = data.compuweighSendComplete;
                this.confirmResults.compuweighSuccess = data.compuweighSuccess;
                this.confirmResults.compuweighResult = data.compuweighResult;
                this.formStatus = FormStatus.Normal;
            },
            error: err => this.errorMessage = err
        });
    }

    trackSentScheduleIds() {
        this.confirmRequest.confirmItems.forEach(confirmItem => {
            confirmItem.dispatches.forEach(dispatch => {
                if (!this.sentScheduleIds.includes(dispatch.scheduleId)) {
                    this.sentScheduleIds.push(dispatch.scheduleId);
                }
            });
        });
    }

    closeConfirmWindow() {
        this.sendComplete.emit([...this.sentScheduleIds]);
        this.resetComponent();
    }

    showLoadsStatus(dispatchId: number) {
        this.loadsStatusDispatchId = dispatchId;
        this.loadsStatusRefresh = !this.loadsStatusRefresh;
        return false;
    }

    startRefreshInterval(): void {
        this.clearRefreshInterval();
        this.intervalId = setInterval(() => {
            this.getTruckers(true);
        }, 10000);
    }

    clearRefreshInterval(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }

    resetComponent() {
        this.clearRefreshInterval();
        this.confirmInfo = new DispatchConfirmInfo();
        this.formState = new SendDispatchFormState();
        this.formStatus = FormStatus.Normal;
        this.confirmRequest = new TruckerConfirmRequest();
        this.truckerList = [];
        this.groupUserList = [];
        this.confirmSendComplete = false;
        this.sentScheduleIds = [];
        this.textSent = null;
        this.emailSent = null;
        this.scoularContactSearch = "";
        this.confirmResults = new TruckerConfirmResults();
    }

    getConfirmFormData(): void {
        if (this.confirmParameters.locCode.length === 0) return;

        this._truckerConfirmService.getConfirmFormData(this.confirmParameters).subscribe({
            next: data => {
                this.confirmInfo = data;
                this.confirmRequest.scoularUserIds.push(this.confirmInfo.currentUserId);
                this.confirmRequest.sendEmail = true;
                this.confirmRequest.loadBreakoutOverride = data.loadBreakoutOverride;
                this.groupUserList = data.groupUserList || [];

                if (!this.truckerId) {
                    this.confirmParameters.filterByUserName = data.currentUserName;
                }
                this.getTruckers(false);
            },
            error: err => this.errorMessage = err
        });
    }

    updateTextToggle() {
        this.confirmRequest.sendText = this.hasValidTextPhones();
    }

    getTruckers(refreshStatus: boolean) {
        if (this.confirmParameters.locCode.length === 0) return;

        const parameters = { ...this.confirmParameters };
        if (refreshStatus) {
            parameters.openStatusOnly = false;
        }

        this._truckerConfirmService.getTruckersWithDispatches(parameters).subscribe({
            next: data => {
                if (refreshStatus) {
                    this.refreshDispatchStatus(data);
                } else {
                    this.initializeTruckers(data);
                }
            },
            error: err => this.errorMessage = err
        });
    }

    initializeTruckers(data: TruckerConfirmItem[]): void {
        this.truckerList = data;
        this.truckerList.forEach(trucker => {
            this.initializeTruckerContacts(trucker);
        });

        this.selectInitialTrucker();
        this.formState.truckerContact = new ErrorState();
        this.checkForInProgressDispatches();
    }

    initializeTruckerContacts(trucker: TruckerConfirmItem): void {
        trucker.truckerContactIds = [];
        const primaryContact = trucker.truckerContacts.find(c => c.isPrimary && c.contactId > 0);

        if (primaryContact) {
            trucker.truckerContactIds.push(primaryContact.contactId);
            this.initializePhoneSelections(primaryContact.contactId, trucker);
        } else {
            trucker.truckerContactIds.push(0);
            this.initializePhoneSelections(0, trucker);
        }

        this.updateTextToggleBasedOnPhoneAvailability();
    }

    selectInitialTrucker(): void {
        if (this.truckerId > 0) {
            const truckerIndex = this.truckerList.findIndex(x => x.truckerId === this.truckerId);
            if (truckerIndex > -1) {
                this.truckerList[truckerIndex].selected = true;
                this.selectTrucker(this.truckerList[truckerIndex]);
            }
        }
    }

    checkForInProgressDispatches(): void {
        const inProcess = this.truckerList.some(t =>
            t.dispatches.some(d => d.emailInProcess || d.rogerInProcess)
        );
        if (inProcess) {
            this.startRefreshInterval();
        }
    }

    refreshDispatchStatus(truckers: TruckerConfirmItem[]) {
        this.truckerList.forEach(truckerItem => {
            const trucker = truckers.find(x => x.truckerId === truckerItem.truckerId);
            if (!trucker) return;

            truckerItem.dispatches.forEach(dispatchItem => {
                const dispatch = trucker.dispatches.find(x => x.dispatchId === dispatchItem.dispatchId);
                if (dispatch) {
                    this.updateDispatchStatus(dispatchItem, dispatch);
                }
            });
        });

        this.checkProcessingComplete();
    }

    updateDispatchStatus(dispatchItem: any, dispatch: any): void {
        dispatchItem.emailStatus = dispatch.emailStatus;
        dispatchItem.rogerStatus = dispatch.rogerStatus;
        dispatchItem.emailInProcess = dispatch.emailInProcess;
        dispatchItem.rogerInProcess = dispatch.rogerInProcess;
    }

    checkProcessingComplete(): void {
        if (this.formStatus !== FormStatus.Saving) return;

        const inProcess = this.truckerList.some(t =>
            t.dispatches.some(d => d.emailInProcess || d.rogerInProcess)
        );

        if (!inProcess) {
            this.clearRefreshInterval();
            this.formStatus = FormStatus.Normal;
            this.confirmSendComplete = true;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['confirmTruckersFormId']) {
            this.loadsStatusModalId = this.confirmTruckersFormId + '-LoadsStatusModal';
        }

        if (changes['scheduleParameters']) {
            this.updateConfirmParameters();
        }

        if (changes['refresh']) {
            this.handleRefreshChange();
        }
    }

    updateConfirmParameters(): void {
        this.confirmParameters.locCode = this.scheduleParameters.locCode;
        this.confirmParameters.periodStartDate = this.scheduleParameters.periodStartDate;
        this.confirmParameters.periodEndDate = this.scheduleParameters.periodEndDate;
    }

    handleRefreshChange(): void {
        this.resetComponent();
        if (this.truckerId > 0) {
            this.confirmParameters.filterByUserName = '';
            this.confirmParameters.openStatusOnly = false;
        } else {
            this.confirmParameters.openStatusOnly = true;
        }
        this.getConfirmFormData();
    }

    onGroupUserChange(): void {
        this.getTruckers(false);
    }

    ngOnInit(): void {
        this.sendClick.pipe(debounceTime(500)).subscribe(() => this.sendConfirmation());
    }

    ngOnDestroy(): void {
        this.clearRefreshInterval();
    }

    filteredTruckerContacts(trucker: TruckerConfirmItem): TruckerContact[] {
        const search = trucker.truckerContactSearch?.toLowerCase() || '';
        return trucker.truckerContacts.filter(item =>
            item.contactName.toLowerCase().includes(search)
        );
    }

    filteredScoularContacts() {
        const search = this.scoularContactSearch?.toLowerCase() || '';
        return this.confirmInfo.scoularContactList.filter(item =>
            item.text.toLowerCase().includes(search)
        );
    }

    toggleTruckerContact(trucker: TruckerConfirmItem, contactId: number): void {
        const index = trucker.truckerContactIds.indexOf(contactId);
        if (index > -1) {
            trucker.truckerContactIds.splice(index, 1);
            this.removePhoneSelections(contactId, trucker.truckerId);
        } else {
            trucker.truckerContactIds.push(contactId);
            this.initializePhoneSelections(contactId, trucker);
        }
        this.truckerContactChange();
        this.updateTextToggleBasedOnPhoneAvailability();
    }

    toggleScoularContact(contactId: number) {
        const index = this.confirmRequest.scoularUserIds.indexOf(contactId);
        if (index > -1) {
            this.confirmRequest.scoularUserIds.splice(index, 1);
        } else {
            this.confirmRequest.scoularUserIds.push(contactId);
        }
        this.scoularContactChange();
    }

    togglePhone(contactId: number, phoneNumber: 1 | 2, truckerId: number) {
        const truckerWithContact = this.truckerList.find(trucker =>
            trucker.truckerId === truckerId
        );

        if (!truckerWithContact) {
            console.error('Could not find trucker with truckerId:', truckerId);
            return;
        }

        let phoneSelection = this.confirmRequest.selectedPhones.find(p =>
            p.contactId === contactId && p.truckerId === truckerId
        );

        if (!phoneSelection) {
            phoneSelection = new ContactPhoneSelection();
            phoneSelection.contactId = contactId;
            phoneSelection.truckerId = truckerId;
            this.confirmRequest.selectedPhones.push(phoneSelection);
        }

        const contact = truckerWithContact.truckerContacts.find(c => c.contactId === contactId);

        if (contact && this.isPhoneDuplicate(contact)) {
            const isSelected = phoneNumber === 1 ? phoneSelection.phone1Selected : phoneSelection.phone2Selected;
            phoneSelection.phone1Selected = !isSelected;
            phoneSelection.phone2Selected = !isSelected;
        } else {
            if (phoneNumber === 1) {
                phoneSelection.phone1Selected = !phoneSelection.phone1Selected;
            } else {
                phoneSelection.phone2Selected = !phoneSelection.phone2Selected;
            }
        }

        this.confirmRequest.sendText = this.hasValidTextPhones();
        this.truckerContactChange();
        this.updateTextToggleBasedOnPhoneAvailability();
    }

    hasValidTextPhones(): boolean {
        return this.hasValidTextPhonesForSelectedTruckers();
    }

    initializePhoneSelections(contactId: number, trucker: TruckerConfirmItem): void {
        const existingIndex = this.confirmRequest.selectedPhones.findIndex(p =>
            p.contactId === contactId && p.truckerId === trucker.truckerId
        );
        if (existingIndex > -1) {
            this.confirmRequest.selectedPhones.splice(existingIndex, 1);
        }

        const phoneSelection = new ContactPhoneSelection();
        phoneSelection.contactId = contactId;
        phoneSelection.truckerId = trucker.truckerId;

        const contact = trucker.truckerContacts.find(c => c.contactId === contactId);

        if (contact) {
            if (this.isPhoneDuplicate(contact)) {
                const shouldSelect = (contact.isPhone1Default && contact.isPhone1TextEnabled) ||
                    (contact.isPhone2Default && contact.isPhone2TextEnabled);

                phoneSelection.phone1Selected = !!shouldSelect;
                phoneSelection.phone2Selected = !!shouldSelect;
            } else {
                const hasTextablePhones = contact.isPhone1TextEnabled || contact.isPhone2TextEnabled;
                if (hasTextablePhones) {
                    phoneSelection.phone1Selected = !!(contact.isPhone1Default && contact.isPhone1TextEnabled);
                    phoneSelection.phone2Selected = !!(contact.isPhone2Default && contact.isPhone2TextEnabled);
                }
            }
        }

        this.confirmRequest.selectedPhones.push(phoneSelection);
    }

    removePhoneSelections(contactId: number, truckerId: number) {
        const index = this.confirmRequest.selectedPhones.findIndex(p =>
            p.contactId === contactId && p.truckerId === truckerId
        );
        if (index > -1) {
            this.confirmRequest.selectedPhones.splice(index, 1);
        }
        this.updateTextToggleBasedOnPhoneAvailability();
    }

    isPhoneSelected(contactId: number, phoneNumber: 1 | 2, truckerId: number): boolean {
        const phoneSelection = this.confirmRequest.selectedPhones.find(p =>
            p.contactId === contactId && p.truckerId === truckerId
        );

        if (!phoneSelection) {
            return false;
        }
        return phoneNumber === 1 ? phoneSelection.phone1Selected : phoneSelection.phone2Selected;
    }

    isPhoneAvailableForText(contact: TruckerContact, phoneNumber: 1 | 2): boolean {
        const isEnabled = phoneNumber === 1 ? contact.isPhone1TextEnabled : contact.isPhone2TextEnabled;
        const phone = phoneNumber === 1 ? contact.phone1 : contact.phone2;

        return isEnabled && phone?.trim().length > 0;
    }

    isPhoneDuplicate(contact: TruckerContact): boolean {
        const phone1 = contact.phone1?.trim();
        const phone2 = contact.phone2?.trim();

        return !!(phone1 && phone2 && phone1 === phone2);
    }

    shouldShowPhone2(contact: TruckerContact): boolean {
        return this.isPhoneAvailableForText(contact, 2) && !this.isPhoneDuplicate(contact);
    }

    getContactByIdInTrucker(contactId: number): TruckerContact | null {
        for (const trucker of this.truckerList) {
            const contact = trucker.truckerContacts.find(c => c.contactId === contactId);
            if (contact) {
                return contact;
            }
        }
        return null;
    }

    getSelectedPhonesForContact(contact: TruckerContact, phoneSelection: ContactPhoneSelection): string[] {
        const selectedPhones: string[] = [];

        if (phoneSelection) {
            if (this.isPhoneDuplicate(contact)) {
                if ((phoneSelection.phone1Selected || phoneSelection.phone2Selected) && contact.phone1?.trim()) {
                    selectedPhones.push(contact.phone1.trim());
                }
            } else {
                if (phoneSelection.phone1Selected && contact.phone1?.trim()) {
                    selectedPhones.push(contact.phone1.trim());
                }
                if (phoneSelection.phone2Selected && contact.phone2?.trim()) {
                    selectedPhones.push(contact.phone2.trim());
                }
            }
        }

        return selectedPhones;
    }

    getSingleContactDisplay(contactId: number, contacts: TruckerContact[], truckerId: number): string {
        const selected = contacts.find(c => c.contactId === contactId);
        if (!selected) return 'Select contact(s)';

        const phoneSelection = this.confirmRequest.selectedPhones.find(p => p.contactId === selected.contactId && p.truckerId === truckerId);
        const selectedPhones = this.getSelectedPhonesForContact(selected, phoneSelection);
        const phoneDisplay = selectedPhones.length > 0 ? ` (${selectedPhones.join(', ')})` : '';

        return `${selected.contactName}${selected.email ? ` (${selected.email})` : ""}${phoneDisplay}`;
    }

    getMultipleContactDisplay(selectedIds: number[], contacts: TruckerContact[], truckerId: number): string {
        const uniquePhones = new Set<string>();

        selectedIds.forEach(contactId => {
            const contact = contacts.find(c => c.contactId === contactId);
            const phoneSelection = this.confirmRequest.selectedPhones.find(p => p.contactId === contactId && p.truckerId === truckerId);

            if (!contact) return;

            const selectedPhones = this.getSelectedPhonesForContact(contact, phoneSelection);
            selectedPhones.forEach(phone => uniquePhones.add(phone));
        });

        const phoneCount = uniquePhones.size;
        const phoneText = phoneCount === 1 ? 'phone selected' : 'phones selected';
        return `${selectedIds.length} selected contacts (${phoneCount} ${phoneText})`;
    }

    getTruckerContactDisplay(trucker: TruckerConfirmItem): string {
        const selectedIds = trucker.truckerContactIds;
        const list = trucker.truckerContacts;

        if (selectedIds.length === 1) {
            return this.getSingleContactDisplay(selectedIds[0], list, trucker.truckerId);
        }

        if (selectedIds.length > 1) {
            return this.getMultipleContactDisplay(selectedIds, list, trucker.truckerId);
        }

        return 'Select contact(s)';
    }

    getScoularContactDisplay(): string {
        const selectedIds = this.confirmRequest.scoularUserIds;
        const list = this.confirmInfo.scoularContactList;

        if (selectedIds.length === 1) {
            const selected = list.find(c => c.id === selectedIds[0]);
            return selected?.text || 'Select contact(s)';
        }

        if (selectedIds.length > 1) {
            return `${selectedIds.length} selected`;
        }

        return 'Select contact(s)';
    }
}